export type InlineReference = {
  id: string;
  /** Opaque contribution namespace owned by the producer. */
  kind: string;
  label: string;
};

export type TiptapContent = {
  type: string;
  attrs?: InlineReference;
  text?: string;
  content?: TiptapContent[];
};

const REFERENCE_PATTERN =
  /@\[((?:\\.|[^\]\\])+)]\(([a-z][a-z0-9.-]*):([^)]+)\)|\[\$([^\]]+)]\(([^)]+[\\/]SKILL\.md)\)/gi;
const TEMPLATE_REFERENCE_PATTERN = /\{\{[^{}]+\}\}/g;

export function parseInlineReferenceValue(
  value: string,
  references: readonly InlineReference[] = [],
  includeTemplateReferences = false,
): TiptapContent {
  const content: TiptapContent[] = [];
  const knownTemplateReferences = new Map(
    references
      .filter((reference) => /^\{\{[^{}]+\}\}$/.test(reference.id))
      .map((reference) => [reference.id, reference]),
  );
  const matches = [
    ...[...value.matchAll(REFERENCE_PATTERN)].map((match) => ({ kind: 'inline' as const, match })),
    ...(includeTemplateReferences
      ? [...value.matchAll(TEMPLATE_REFERENCE_PATTERN)].map((match) => ({
          kind: 'template' as const,
          match,
        }))
      : []),
  ].sort((left, right) => (left.match.index ?? 0) - (right.match.index ?? 0));
  let offset = 0;
  for (const entry of matches) {
    const { match } = entry;
    const index = match.index;
    if (index < offset) continue;
    if (index > offset) appendText(content, value.slice(offset, index));
    const templateReference =
      entry.kind === 'template' ? knownTemplateReferences.get(match[0]) : undefined;
    content.push({
      type: 'reference',
      attrs:
        templateReference ??
        (entry.kind === 'template'
          ? { id: match[0], kind: 'template', label: match[0].slice(2, -2) }
          : {
              label: match[4] ?? decodeAgentMentionLabel(match[1] ?? ''),
              kind: (match[4] === undefined
                ? (match[2] ?? 'reference')
                : 'skill') as InlineReference['kind'],
              id: match[5] ?? match[3] ?? '',
            }),
    });
    offset = index + match[0].length;
  }
  if (offset < value.length) appendText(content, value.slice(offset));
  return { type: 'doc', content: [{ type: 'paragraph', content }] };
}

function appendText(content: TiptapContent[], value: string): void {
  value.split('\n').forEach((part, index) => {
    if (index > 0) content.push({ type: 'hardBreak' });
    if (part.length > 0) content.push({ type: 'text', text: part });
  });
}

export function encodeAgentMentionLabel(label: string): string {
  return label.replaceAll('\\', '\\\\').replaceAll(']', '\\]');
}
export function decodeAgentMentionLabel(label: string): string {
  return label.replace(/\\([\\\]])/g, '$1');
}

const SKILL_MENTION_PATTERN = /\[\$([^\]]+)]\(([^)]+[\\/]SKILL\.md)\)/gi;
// A reference cut at the end of a text (a truncated title) still reads as its label.
const MENTION_PATTERN = /@\[((?:\\.|[^\]\\])+)]\(([a-z][a-z0-9.-]*):([^)]*)(?:\)|$)/gi;

/**
 * A message's text with its `@[label](kind:id)` references as their labels, for where the text
 * is read rather than resolved: `'markdown'` keeps them apart as `**@label**` (a link to an
 * unknown scheme would be blocked), `'plain'` writes `@label`. Skills read as `$name` likewise.
 */
export function readableReferences(text: string, format: 'markdown' | 'plain'): string {
  const emphasized = (value: string) =>
    format === 'plain' ? value : `**${value.replace(/[*_`\\]/g, '\\$&')}**`;
  return (
    text
      .replace(MENTION_PATTERN, (_token, label: string) =>
        emphasized(`@${decodeAgentMentionLabel(label)}`),
      )
      // A skill's link (`skill://…`) is no address a reader can open: markdown would block it.
      .replace(SKILL_MENTION_PATTERN, (_token, label: string) => emphasized(`$${label}`))
  );
}
